import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import { Button } from "@/components/ui/button";

import { LandingDemoButton, LandingStage } from "./LandingDemo";

/**
 * The first screen: what this is, the way in, and the room itself to press.
 *
 * The heading says the one thing the product is built around — a link, and you
 * are talking — and the window beside it shows it rather than describing it
 * (#942).
 */
export function LandingHero({
  started,
  onDemo,
}: {
  started: boolean;
  onDemo: () => void;
}) {
  const { t } = useTranslation();
  const promises = ["browser", "borrowed", "recorded"] as const;

  return (
    <section
      id="top"
      aria-labelledby="landing-title"
      className="bg-[radial-gradient(900px_600px_at_78%_45%,hsl(var(--primary)/0.13),transparent_70%)]"
    >
      <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-4 pt-12 pb-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.12fr)] lg:gap-14 lg:pt-20">
        <div className="text-center lg:text-left">
          <h1
            id="landing-title"
            className="text-balance text-xl font-bold tracking-tight"
          >
            {t("landing.hero.title")}{" "}
            <span className="bg-gradient-to-b from-primary/70 to-primary bg-clip-text text-transparent">
              {t("landing.hero.titleAccent")}
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-pretty text-lg text-muted-foreground lg:mx-0">
            {t("landing.hero.pitch")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start">
            <Button asChild size="lg">
              <a href="/home" data-testid="landing-create">
                {t("landing.hero.create")}
              </a>
            </Button>
            <LandingDemoButton onStart={onDemo} />
          </div>
          <ul className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground lg:justify-start">
            {promises.map((promise) => (
              <li key={promise} className="flex items-center gap-1.5">
                <Check className="size-4 text-primary" aria-hidden="true" />
                {t(`landing.hero.promise.${promise}`)}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <LandingStage started={started} />
          <p className="mt-3 text-center text-xs text-muted-foreground">
            {t(started ? "landing.room.demoHint" : "landing.room.hint")}
          </p>
        </div>
      </div>
    </section>
  );
}
